import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Button,
  IconButton,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import { useNotificationsInbox } from './hooks/useNotificationsInbox';
import { getNotificationConfig, linkify } from './notificationConfig';

const CLAMP_TWO_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
};

export default function ClinicNotificationsMobile() {
  const {
    notifications,
    isLoading,
    isMarking,
    selected,
    setSelected,
    markAsRead,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useNotificationsInbox({ autoSelectFirst: false });

  return (
    <>
      <Loading isToHidden={!isLoading && !isMarking} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        {selected ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <IconButton onClick={() => setSelected(null)} aria-label="Voltar para a lista">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Notificação
              </Typography>
            </Box>

            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
                {selected.title}
              </Typography>

              <Chip
                label={!selected.read ? 'Não lida' : 'Lida'}
                color={!selected.read ? 'primary' : 'default'}
                size="small"
                sx={{ mt: 1 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                dangerouslySetInnerHTML={{ __html: linkify(selected.description ?? '') }}
              />

              {!selected.read && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DoneIcon />}
                  onClick={() => markAsRead(selected.id)}
                  sx={{ mt: 3 }}
                >
                  Marcar como lida
                </Button>
              )}
            </CardContent>
          </>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Minhas Notificações
              </Typography>
            </Box>

            {notifications.length === 0 && !isLoading ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Nenhuma notificação encontrada.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {notifications.map((notification, index) => {
                  const config = getNotificationConfig(notification);

                  return (
                    <React.Fragment key={notification.id}>
                      <ListItemButton
                        alignItems="flex-start"
                        onClick={() => setSelected(notification)}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: 'transparent',
                              color: config.color,
                            }}
                          >
                            {config.icon}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={!notification.read ? 'bold' : 'normal'}
                              sx={CLAMP_TWO_LINES}
                            >
                              {notification.title}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={CLAMP_TWO_LINES}
                              >
                                {notification.description}
                              </Typography>
                              {!notification.read && (
                                <Chip
                                  label="Não lida"
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </>
                          }
                          slotProps={{ secondary: { component: 'div' } }}
                        />

                        <ChevronRightIcon
                          sx={{ color: 'text.disabled', alignSelf: 'center', ml: 1 }}
                        />
                      </ListItemButton>
                      {index < notifications.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </>
        )}
      </Card>
    </>
  );
}
